import React, { useEffect, useState } from 'react';
import { graphql, useFragment } from 'utils/graphClient';
import {
  EditableFolderItemMenu_folderItemNode$data,
  EditableFolderItemMenu_folderItemNode$key,
} from './__generated__/EditableFolderItemMenu_folderItemNode.graphql';
import { Button, useDisclosure } from '@cardboard-ui/react';
import { t } from '@lingui/macro';
import { Layout, useActiveLayout } from 'utils/LayoutProvider';
import { PublishFolderItemDialog } from './PublishFolderItemDialog';

const folderNodeFragment = graphql`
  fragment EditableFolderItemMenu_folderItemNode on FolderNodeInterface {
    __typename
    canPublish {
      value
    }
    publishState
    ... on FolderItem {
      id
      content {
        __typename
        ... on Page {
          id
          data
        }
        ... on Form {
          id
        }
      }
      ...PublishFolderItemDialog_folderItem
    }
  }
`;

interface EditableFolderItemMenuProps {
  fragment: EditableFolderItemMenu_folderItemNode$key;
  isUpdatingPage: boolean;
}

export const EditableFolderItemMenu = ({
  fragment,
  isUpdatingPage,
}: EditableFolderItemMenuProps) => {
  const folderItem = useFragment(folderNodeFragment, fragment);

  const isPage = folderItem.content?.__typename === 'Page';
  const isForm = folderItem.content?.__typename === 'Form';

  if (!(isPage || isForm)) {
    return null;
  }

  return (
    <>
      <FolderItemPublishOption folderItem={folderItem} />
      {isPage && <PageHeaderSaveOption isUpdatingPage={isUpdatingPage} />}
    </>
  );
};

interface PageHeaderSaveOptionProps {
  isUpdatingPage: boolean;
}

const PageHeaderSaveOption = ({
  isUpdatingPage,
}: PageHeaderSaveOptionProps) => {
  const [showSaveButton, setShowSaveButton] = useState(false);
  const activeLayout = useActiveLayout();
  const isMobile = activeLayout !== Layout.DESKTOP;

  // Once we have made a change, always show the save status
  useEffect(() => {
    if (isUpdatingPage) {
      setShowSaveButton(true);
    }
  }, [isUpdatingPage]);

  // Never display this dedicated button on mobile, there is no room for it
  // Instead rely on the DiscardConfirmationDialog to ask the user to save
  if (!showSaveButton || isMobile) return null;

  return (
    <Button isDisabled size="xs" variant="outline">
      {isUpdatingPage ? t`Saving changes` : t`All changes saved`}
    </Button>
  );
};

interface PageHeaderPublishOptionProps {
  folderItem: EditableFolderItemMenu_folderItemNode$data;
}

const FolderItemPublishOption = ({
  folderItem,
}: PageHeaderPublishOptionProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const isPublished = folderItem.publishState === 'PUBLISHED';

  return (
    <>
      <PublishFolderItemDialog
        folderItem={folderItem}
        isOpen={isOpen}
        onClose={onClose}
      />

      {!isPublished && (
        <Button
          variant="outline"
          size="xs"
          colorScheme="orange"
          onClick={onOpen}
        >
          {t`Draft`}
        </Button>
      )}
    </>
  );
};
